import { releaseMediaSrc } from '@/helpers/deferredMediaSrc';
import { getSwiperInstance } from '@/helpers/swiperInstance';
import { findNeighborSlides, loadMediaWindow, slideStep } from '@/helpers/reelMediaLoadWindow';

// PC ビューアのメディア読み込み順。
// - ストーリー型: 中央 → 右隣（グループ内の次）→ 次グループの先頭 → 左隣（グループ内の前）→ 前グループの先頭
// - リール型・タイル型: 中央 → 右隣 → 左隣
// PC はグループ移動のたびにスライドを作り直すため、隣のグループの先頭は画面に出ない要素で先に取得しておき、
// 移動後に作られるスライドではブラウザのキャッシュから読ませる。
const PRELOAD_HOLDER_CLASS = 'storeeel-group-preload-holder';

const ensurePreloadHolder = (anchorEl) => {
  const root = anchorEl?.closest?.('.storeeel-modal') || document.body;
  let holder = root.querySelector(`:scope > .${PRELOAD_HOLDER_CLASS}`);
  if (!holder) {
    holder = document.createElement('div');
    holder.className = PRELOAD_HOLDER_CLASS;
    holder.setAttribute('aria-hidden', 'true');
    holder.style.display = 'none';
    root.appendChild(holder);
  }
  return holder;
};

const groupFirstUrl = (group) => group?.contents?.[0]?.mediaAsset?.mediaUrl || null;

// 先読み置き場には今の前後グループの分だけを残す（移動のたびに増え、取得中の通信が溜まるのを防ぐ）
const pruneGroupPreloadHolder = (anchorEl, groups) => {
  const root = anchorEl?.closest?.('.storeeel-modal') || document.body;
  const holder = root.querySelector(`:scope > .${PRELOAD_HOLDER_CLASS}`);
  if (!holder) return;
  const keepUrls = new Set(groups.map(groupFirstUrl).filter(Boolean));
  Array.from(holder.children).forEach((el) => {
    if (keepUrls.has(el.getAttribute('src'))) return;
    releaseMediaSrc(el);
    el.remove();
  });
};

// 隣のグループの先頭コンテンツを画面に出さずに取得する step を作る
const groupFirstStep = (group, anchorEl) => () => {
  const asset = group?.contents?.[0]?.mediaAsset;
  const url = asset?.mediaUrl;
  if (!url) return [];
  // 表示中のグループ自身（グループが1つでループする場合など）は対象外
  if (String(group.id) === String(window.iconId)) return [];
  const holder = ensurePreloadHolder(anchorEl);
  const existing = Array.from(holder.children).find((el) => el.getAttribute('src') === url);
  if (existing) return [existing];

  const isImage = (asset.attachmentData?.metadata?.mime_type || '').includes('image');
  // .storeeel-movie / .storeeel-media-image を付けず、再生・進捗・計測の対象にしない
  const el = document.createElement(isImage ? 'img' : 'video');
  if (!isImage) {
    el.muted = true;
    el.setAttribute('muted', '');
    el.setAttribute('playsinline', '');
    el.preload = 'auto';
  }
  el.src = url;
  holder.appendChild(el);
  if (!isImage) {
    try { el.load(); } catch { void 0; }
  }
  return [el];
};

export const loadPcMediaWindow = (activeSlide) => {
  if (!activeSlide) return;
  const { nextSlide, prevSlide } = findNeighborSlides(activeSlide);
  const keepSlides = [nextSlide, prevSlide];
  // PC は遷移の開始時に呼ばれるため、出ていくスライドが画面から消える（遷移が終わる）まで解放を待つ
  const speed = getSwiperInstance()?.params?.speed;
  const releaseDelayMs = (Number.isFinite(speed) ? speed : 400) + 100;
  if (window.storeeelItems?.frame === 'circle') {
    pruneGroupPreloadHolder(activeSlide, [window.nextGroup, window.prevGroup]);
    loadMediaWindow(activeSlide, [
      slideStep(nextSlide),
      groupFirstStep(window.nextGroup, activeSlide),
      slideStep(prevSlide),
      groupFirstStep(window.prevGroup, activeSlide),
    ], { keepSlides, releaseDelayMs });
    return;
  }
  loadMediaWindow(activeSlide, [slideStep(nextSlide), slideStep(prevSlide)], { keepSlides, releaseDelayMs });
};
